import getEwsRequestDateString from 'owa-mail-store/lib/services/utils/getEwsRequestDateString';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import itemId from 'owa-service/lib/factory/itemId';
import applyConversationActionService from 'owa-mail-store/lib/services/applyConversationActionService';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import RequestOptions from 'owa-service/lib/RequestOptions';

/**
 * Create a ConversationAction with delete params for a given conversation.
 * @param conversationContext the conversation context to be deleted
 * @param contextFolderId  the folder where the conversations will be deleted from
 * @param disposalType  the disposal type
 * @returns a ConversationAction
 */
function createDeleteConversationAction(
    conversationContext: ApplyConversationActionContext,
    contextFolderId: string,
    disposalType: DisposalType
): ConversationAction {
    return conversationAction({
        Action: 'Delete',
        ConversationId: itemId({ Id: conversationContext.conversationId }),
        DeleteType: disposalType,
        ConversationLastSyncTime: getEwsRequestDateString(
            conversationContext.conversationLastSyncTimeStamp
        ),
        SuppressReadReceipts: true,
        ContextFolderId: getServiceContextFolderId(contextFolderId),
    });
}

/**
 * Worker method which converts the provided data into a delete ApplyConversationActionRequest.
 * @param conversationContexts the conversation contexts to be deleted
 * @param contextFolderId  the folder where the conversations will be deleted from
 * @param disposalType the disposal type
 * @returns an ApplyConversationActionRequest
 */
function configureRequestBody(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    disposalType: DisposalType
): ApplyConversationActionRequest {
    const conversationActions = conversationContexts.map(conversationContext =>
        createDeleteConversationAction(conversationContext, contextFolderId, disposalType)
    );

    return applyConversationActionRequest({
        ConversationActions: conversationActions,
        ReturnMovedItemIds: false,
    });
}

/**
 * The service which calls the ApplyConversationAction with delete parameters.
 * @param conversationContexts the conversation contexts to be deleted
 * @param contextFolderId  the folder where the conversations will be deleted from
 * @param disposalType the disposal type
 */
export default function deleteConversationService(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    disposalType: DisposalType,
    options?: RequestOptions
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(conversationContexts, contextFolderId, disposalType);

    // Work Item 27629: Remove workaround for timezone issue (sending requests in UTC time zone)
    const jsonRequestHeader = getJsonRequestHeader();
    jsonRequestHeader.TimeZoneContext.TimeZoneDefinition.Id = 'UTC';

    return applyConversationActionService(requestBody, jsonRequestHeader, options);
}
